import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Globe, Smartphone, Search, BarChart3, CheckCircle, Star, type LucideIcon } from 'lucide-react'
import { BentoGrid, BentoCard } from '@/components/ui/BentoGrid'
import { Button } from '@/components/ui/Button'
import { siteConfig } from '@/config/site'
import { SEO } from '@/components/SEO'

const iconMap: Record<string, LucideIcon> = {
  Globe,
  Smartphone,
  Search,
  BarChart3,
}

const services = siteConfig.services.map((service, index) => ({
  title: service.name,
  description: service.shortDescription,
  icon: iconMap[service.icon] || Globe,
  bgImage: index === 0
    ? 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2670&auto=format&fit=crop'
    : index === 1
    ? 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?q=80&w=1000&auto=format&fit=crop'
    : index === 2
    ? 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1000&auto=format&fit=crop'
    : undefined,
  className: index === 0
    ? 'md:col-span-2 md:row-span-2 min-h-[400px]'
    : 'md:col-span-1 md:row-span-1 min-h-[250px]',
  price: service.price,
}))

export function Home() {
  return (
    <>
      <SEO
        title="Websites, Apps & Marketing for UK Small Businesses"
        description="We help UK small businesses grow online with affordable websites from £499, mobile apps, SEO, and social media marketing. No tech jargon, just results. Get a free quote today."
        keywords={[
          'web design UK',
          'small business website',
          'affordable website design',
          'UK web agency',
          'mobile app development UK',
          'SEO services London',
          'social media marketing UK',
          'website builder for small business',
        ]}
        url="https://nexaigen.co.uk/"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="w-full"
      >
        {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-background">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/20 via-background to-background" />
        <div className="absolute inset-0 bg-grid opacity-50" />

        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '1s' }} />

        <div className="container relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-5xl mx-auto"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/40 border border-slate-700/50 backdrop-blur-md mb-8 hover:border-primary/30 transition-colors cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
              </span>
              <span className="text-xs font-semibold tracking-wider text-slate-300 uppercase">
                {siteConfig.tagline}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-8xl font-black tracking-tighter leading-[1] mb-8 text-white">
              Grow Your Business <br />
              <span className="gradient-text">Online</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-2xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed font-light">
              We make websites, apps, and marketing simple for UK small businesses.
              No tech jargon. Just results you can see.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="group relative overflow-hidden">
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-slate-200 to-transparent -translate-x-full group-hover:animate-shimmer" />
                  <span className="relative flex items-center gap-2">
                    Get a Free Quote <ArrowRight size={18} />
                  </span>
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="secondary" size="lg">
                  See What We Do
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-slate-400">
              {siteConfig.stats.map((stat) => (
                <div key={stat.label} className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-white">{stat.value}</span>
                  <span className="text-sm">{stat.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="section bg-background relative border-t border-white/5">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
                What We <span className="text-primary">Do</span>
              </h2>
              <p className="text-slate-400 text-lg">
                Simple solutions to help your business succeed online.
                We handle the tech so you can focus on your customers.
              </p>
            </div>
            <Link
              to="/services"
              className="text-white font-semibold border-b border-primary pb-1 hover:text-primary transition-colors inline-flex items-center gap-2"
            >
              View All Services <ArrowRight size={16} />
            </Link>
          </div>

          <BentoGrid>
            {services.map((service, index) => (
              <BentoCard
                key={service.title}
                index={index}
                className={service.className}
                title={service.title}
                description={service.description}
                icon={service.icon}
                bgImage={service.bgImage}
              />
            ))}
          </BentoGrid>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section bg-background relative">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why UK Businesses <span className="text-primary">Choose Us</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              We are not a big agency with confusing prices. We are a small team that treats your business like our own.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {siteConfig.whyChooseUs.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-slate-900/50 border border-white/5 hover:border-primary/30 transition-colors"
              >
                <CheckCircle className="text-accent mb-4" size={28} />
                <h3 className="text-xl font-bold text-white mb-2">{reason.title}</h3>
                <p className="text-slate-400 text-sm">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section bg-background relative border-t border-white/5">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              What Our <span className="text-primary">Customers Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {siteConfig.testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-3xl bg-slate-900/50 border border-white/5"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 text-lg leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-bold text-white">{testimonial.name}</p>
                  <p className="text-sm text-slate-400">{testimonial.business}, {testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Grow Your Business?
            </h2>
            <p className="text-xl text-slate-400 mb-10">
              Book a free chat with us. No pressure, no obligation.
              Just a friendly conversation about how we can help.
            </p>
            <Link to="/contact">
              <Button size="lg">
                Book Your Free Chat <ArrowRight size={18} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
      </motion.div>
    </>
  )
}

export default Home
